import HCaptcha from '@hcaptcha/react-hcaptcha';
import {Database} from '@tnc/shared';
import {useRef, useState} from 'react';

import {brandT} from '@/lib/types/brand.types';
import {distributorT} from '@/lib/types/distributor.types';
import {retailerT} from '@/lib/types/retailer.types';

import {SupabaseInstance} from '@/lib/utils';

import ListingItems from '@/pages/speculators/settings/components/ListingItems';
import RegisterBrand from '@/pages/speculators/settings/components/RegisterBrand';
import RegisterDistributor from '@/pages/speculators/settings/components/RegisterDistributor';
import RegisterRetailer, {RegisterRetailerRefT} from '@/pages/speculators/settings/components/RegisterRetailer';
import UserDataForm from '@/pages/speculators/settings/components/UsersDataForm';

import {useDataQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {SearchAndSelectComponent} from '@/components';
import Button from '@/components/Button';
import {Modal, ModalRefT} from '@/components/Modal';
import Page from '@/components/Page';
import {MapStructureT} from '@/components/searchAndSelectComponent';

import ConfirmApplicationForm from './components/ConfirmApplicationForm';

type EntityT = brandT | retailerT | distributorT;

function SpeculatorSettingsPage() {
  const {
    userAccounts: {speculator, brands, retailers, distributors},
    handleResendEmailVerification,
  } = useAuth();
  const {toast} = useNotifications();

  // State
  const [searchAndSelectState, setSearchAndSelectState] = useState<{
    isVisible: boolean;
    type: 'brand' | 'retailer' | 'distributor';
  }>({
    isVisible: false,
    type: 'brand',
  });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedEntity, setSelectedEntity] = useState<brandT | retailerT | distributorT | null>(null);
  const [disabledResetBtn, setDisabledResetBtn] = useState<boolean>(true);
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();

  // Refs
  const captchaRef = useRef<HCaptcha | null>(null);
  const registerDistributorModalRef = useRef<ModalRefT | null>(null);
  const registerBrandModalRef = useRef<ModalRefT | null>(null);
  const registerRetailerRef = useRef<RegisterRetailerRefT | null>(null);
  const confirmApplicationModalRef = useRef<ModalRefT | null>(null);

  const {data: {data: Applications} = {data: []}} = useDataQuery({
    table: 'Applications',
    columns: ['*'],
    filters: [
      {
        column: 'fk_speculator_id',
        value: speculator?.speculator_id,
        operator: 'eq',
      },
      {
        column: 'application_status',
        value: 'pending',
        operator: 'eq',
      },
    ],
    searchTerm: searchTerm,
    searchColumn: 'application_entity_id',
    enabled: !!speculator?.speculator_id,
  });

  const retailerApplications = Applications.filter(application => application.application_entity_type === 'retailer');
  const brandApplications = Applications.filter(application => application.application_entity_type === 'brand');
  const distributorApplications = Applications.filter(
    application => application.application_entity_type === 'distributor'
  );

  /***********************************
   * QUERIES
   ***********************************/
  const {data: {data: Distributors} = {data: []}} = useDataQuery({
    table: 'Distributor',
    columns: ['*'],
    searchTerm: searchTerm,
    searchColumn: 'distributor_name',
    enabled: searchAndSelectState.isVisible && searchAndSelectState.type === 'distributor',
  });

  const {data: {data: Brands} = {data: []}} = useDataQuery({
    table: 'Brand',
    columns: ['*'],
    searchTerm: searchTerm,
    searchColumn: 'brand_name',
    enabled: searchAndSelectState.isVisible && searchAndSelectState.type === 'brand',
  });

  const {data: {data: Retailers} = {data: []}} = useDataQuery({
    table: 'Retailer',
    columns: ['*'],
    searchTerm: searchTerm,
    searchColumn: 'retailer_name',
    enabled: searchAndSelectState.isVisible && searchAndSelectState.type === 'retailer',
  });

  // Functions
  const handleResetPassword = async () => {
    const user = await SupabaseInstance.getCurrentUser();

    if (!user || !user.email) {
      throw new Error('No user found');
    }

    setDisabledResetBtn(true);

    try {
      await handleResendEmailVerification(user.email, captchaToken);
      toast.success('Password reset email sent successfully');
    } catch (error) {
      console.error('Failed to send password reset email', error);
      toast.error('Failed to send password reset email, please wait 5 seconds and try again');
    } finally {
      setCaptchaToken(undefined);
      captchaRef.current?.resetCaptcha();
      setTimeout(() => {
        setDisabledResetBtn(false);
      }, 5000);
    }
  };

  // Function to handle selection
  const handleSelect = (item: brandT | retailerT | distributorT) => {
    setSelectedEntity(item);
    confirmApplicationModalRef.current?.show();
  };

  const handleCancel = () => {
    setSelectedEntity(null);
    confirmApplicationModalRef.current?.hide();
  };

  const handleSubmitApplication = async (sentData: {email: string; phone: string; notes: string}) => {
    const id = selectedEntity ? getEntityId(selectedEntity, searchAndSelectState.type) : null;
    const speculatorId = speculator?.speculator_id;

    if (!id || !speculatorId) {
      console.error('No entity selected or speculator ID is missing');
      return;
    }

    // Make sure user does not have a pending application for this entity
    const {data: applications} = await SupabaseInstance.supabase
      .from('Applications')
      .select('*')
      .eq('fk_speculator_id', speculatorId)
      .eq('application_entity_id', id)
      .eq('application_entity_type', searchAndSelectState.type as Database['public']['Enums']['role_entity_type'])
      .eq('application_status', 'pending');

    if (applications?.length) {
      confirmApplicationModalRef.current?.hide();
      toast.error('You already have an application for this entity');
      return;
    }

    try {
      const {error} = await SupabaseInstance.supabase.from('Applications').insert({
        application_entity_id: id,
        application_entity_type: searchAndSelectState.type as Database['public']['Enums']['role_entity_type'],
        application_contact_email: sentData.email,
        application_contact_number: sentData.phone,
        application_notes: sentData.notes,
        fk_speculator_id: speculatorId,
        application_status: 'pending',
        date_created: new Date().toISOString(),
      });

      if (error) {
        throw new Error('Failed to submit application');
      }

      toast.success('Application submitted successfully');
      confirmApplicationModalRef.current?.hide();
    } catch (error) {
      console.error('Error submitting application', error);
      toast.error('Error submitting application');
    }

    setSelectedEntity(null);
    confirmApplicationModalRef.current?.hide();
  };

  const getEntityId = (entity: EntityT, type: string) => {
    switch (type) {
      case 'brand':
        return (entity as brandT).brand_id;
      case 'retailer':
        return (entity as retailerT).retailer_id;
      case 'distributor':
        return (entity as distributorT).distributor_id;
      default:
        return null;
    }
  };

  // Mapping fields based on the type of business
  const mapStructure: MapStructureT<EntityT> = {
    displayFields: [
      {
        title: 'Name',
        field:
          searchAndSelectState.type === 'brand'
            ? 'brand_name'
            : searchAndSelectState.type === 'retailer'
              ? 'retailer_name'
              : 'distributor_name',
      },
      {
        title: 'Email',
        field:
          searchAndSelectState.type === 'brand'
            ? 'brand_email'
            : searchAndSelectState.type === 'retailer'
              ? 'retailer_email'
              : 'distributor_email',
      },
      {
        title: 'Status',
        field:
          searchAndSelectState.type === 'brand'
            ? 'status'
            : searchAndSelectState.type === 'retailer'
              ? 'retailer_status'
              : 'status',
      },
    ],
    valueField:
      searchAndSelectState.type === 'brand'
        ? 'brand_id'
        : searchAndSelectState.type === 'retailer'
          ? 'retailer_id'
          : 'distributor_id',
  };

  return (
    <>
      <Page currentPage="Settings">
        <div className="grid grid-cols-12 gap-x-6">
          <div className="col-span-12 xl:col-span-6 xxl:col-span-6">
            <div className="box">
              <div className="box-header">
                <h4 className="box-title">Personal Account Settings</h4>
              </div>
              <div className="box-body">
                <UserDataForm />
              </div>
            </div>
          </div>
          <div className="col-span-12 xl:col-span-6 xxl:col-span-6">
            <div className="box">
              <div className="box-header">
                <h4 className="box-title">Preferences</h4>
              </div>
              <div className="box-body">
                <HCaptcha
                  ref={captchaRef}
                  sitekey={import.meta.env.VITE_HCAPTCHA_SITE_KEY}
                  onVerify={token => {
                    setCaptchaToken(token);
                    setDisabledResetBtn(false);
                  }}
                  onExpire={() => {
                    setCaptchaToken(undefined);
                    setDisabledResetBtn(true);
                  }}
                />
                <Button
                  className="mt-4"
                  disabled={disabledResetBtn}
                  onClick={() => handleResetPassword()}
                  variant="outline"
                  color="danger"
                  icon="lock"
                >
                  Send Password Reset Email
                </Button>
              </div>
            </div>

            <div className="box overflow-hidden">
              <div className="bg-white">
                <div className="box-header">
                  <h4 className="box-title">
                    <span className="text-primary">Register a Business</span>
                  </h4>
                </div>
                <div className="border-t border-slate-400/30">
                  <div className="box-body space-y-4">
                    <ListingItems
                      items={retailers}
                      entityType="retailer"
                      onRegisterClick={() => registerRetailerRef.current?.show()}
                      onLinkClick={() => setSearchAndSelectState({isVisible: true, type: 'retailer'})}
                      applications={retailerApplications}
                    />
                    <ListingItems
                      items={brands}
                      entityType="brand"
                      onRegisterClick={() => registerBrandModalRef.current?.show()}
                      onLinkClick={() => setSearchAndSelectState({isVisible: true, type: 'brand'})}
                      applications={brandApplications}
                    />
                    <ListingItems
                      items={distributors}
                      entityType="distributor"
                      onRegisterClick={() => registerDistributorModalRef.current?.show()}
                      onLinkClick={() => setSearchAndSelectState({isVisible: true, type: 'distributor'})}
                      applications={distributorApplications}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Page>
      <SearchAndSelectComponent<EntityT>
        isVisible={searchAndSelectState.isVisible}
        onClose={() => setSearchAndSelectState({isVisible: false, type: 'brand'})}
        overrideClose
        onSelect={handleSelect}
        onSearch={setSearchTerm}
        results={
          searchAndSelectState.type === 'brand'
            ? Brands
            : searchAndSelectState.type === 'retailer'
              ? Retailers
              : Distributors
        }
        title={`Search and Select ${searchAndSelectState.type}`}
        mapStructure={mapStructure}
        createNewLabel={`Create New ${searchAndSelectState.type}`}
      />
      <RegisterRetailer ref={registerRetailerRef} />
      <RegisterDistributor ref={registerDistributorModalRef} />
      <RegisterBrand ref={registerBrandModalRef} />
      <Modal title="Confirm Application" ref={confirmApplicationModalRef} size="xl">
        <ConfirmApplicationForm
          entity={selectedEntity as EntityT}
          entityType={searchAndSelectState.type}
          onCancel={handleCancel}
          onSubmit={handleSubmitApplication}
        />
      </Modal>
    </>
  );
}

export default SpeculatorSettingsPage;
